import React, { type FC } from 'react';
import Image from 'next/image';
import { icons as lucideIcons } from 'lucide-react';
import * as remixIcons from '@remixicon/react';
import { cn } from '../../utils/cn';
import { type IconProps, type IconType, iconSizeMap } from './icon.types';

/**
 * Get numeric size from IconSize
 */
const getSize = (size: IconProps['size']): number => {
    if (typeof size === 'number') return size;
    return iconSizeMap[size ?? 'xlarge'] ?? 24;
};

/**
 * Convert icon name to proper format for each library
 * Lucide: 'home' -> 'Home', 'shopping-cart' -> 'ShoppingCart'
 * Remix: 'home' -> 'RiHomeLine', 'arrow-left' -> 'RiArrowLeftLine'
 */
const normalizeIconName = (name: string, type: IconType): string => {
    if (type === 'remix') {
        // Handle Remix icon naming: 'home' -> 'RiHomeLine', 'home-fill' -> 'RiHomeFill'
        // If already starts with 'Ri', return as-is
        if (name.startsWith('Ri')) return name;

        // Convert kebab-case to PascalCase and add Ri prefix
        const hasFill = name.endsWith('-fill');
        const baseName = name.replace(/-fill$/, '').replace(/-line$/, '');

        const pascalCase = baseName
            .split('-')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join('');

        const suffix = hasFill ? 'Fill' : 'Line';
        return `Ri${pascalCase}${suffix}`;
    }

    // Lucide: convert to PascalCase
    // 'home' -> 'Home', 'shopping-cart' -> 'ShoppingCart', 'arrow-left' -> 'ArrowLeft'
    if (name.includes('-')) {
        return name
            .split('-')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join('');
    }

    // Already PascalCase or single word
    return name.charAt(0).toUpperCase() + name.slice(1);
};

/**
 * Get icon component from library by name
 */
const getIconComponent = (name: string, type: IconType) => {
    const normalizedName = normalizeIconName(name, type);

    if (type === 'remix') {
        return (
            remixIcons as Record<string, React.ComponentType<{ size?: number; color?: string; className?: string }>>
        )[normalizedName];
    }

    // Default: lucide
    return lucideIcons[normalizedName as keyof typeof lucideIcons];
};

/**
 * Icon Component - Unified icon component with name-based lookup
 *
 * Supports:
 * - Lucide icons (default): 1500+ icons, modern design
 * - Remix icons: 2800+ icons, multiple styles
 * - Direct icon component via `icon` prop
 * - Inline SVG via `children`
 *
 * @example
 * // By name (Lucide - default)
 * <Icon name="Home" size="large" />
 * <Icon name="shopping-cart" size="xlarge" />
 * <Icon name="ArrowLeft" className="text-[var(--color-primary-100)]" />
 *
 * @example
 * // Remix icons
 * <Icon type="remix" name="home-line" size="large" />
 * <Icon type="remix" name="shopping-cart-fill" />
 *
 * @example
 * // Direct component (tree-shaking friendly)
 * import { Home } from 'lucide-react';
 * <Icon icon={Home} size="large" />
 *
 * @example
 * // Inline SVG
 * <Icon size="xlarge" className="text-[var(--color-error-100)]">
 *   <path d="M12 2L2 7l10 5 10-5-10-5z" />
 * </Icon>
 */
const Icon: FC<IconProps> = ({
    name,
    type = 'lucide',
    dataUriProps,
    icon: IconComponentProp,
    size = 'xlarge',
    color,
    className,
    containerClassName,
    containerStyle,
    disabled = false,
    showIf = true,
    strokeWidth = 2,
    fill,
    stroke,
    children,
    onClick,
    testID = 'icon',
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    ...svgProps
}) => {
    // Early return if hidden
    if (!showIf) return null;

    const iconSize = getSize(size);

    // Container classes with Tailwind
    const containerClasses = cn(
        'inline-flex items-center justify-center shrink-0',
        disabled && 'opacity-50 pointer-events-none',
        onClick && !disabled && 'cursor-pointer',
        containerClassName
    );

    // Icon classes
    const iconClasses = cn('shrink-0', className);

    // 1. Direct icon component prop (highest priority)
    if (IconComponentProp) {
        return (
            <span
                data-testid={testID}
                className={containerClasses}
                style={containerStyle}
                onClick={disabled ? undefined : onClick}
                aria-label={ariaLabel ?? name}
                aria-hidden={ariaHidden}
            >
                <IconComponentProp size={iconSize} color={color} strokeWidth={strokeWidth} className={iconClasses} />
            </span>
        );
    }

    // 2. Data URI icon rendering
    if (type === 'data-uri') {
        if (!dataUriProps?.src) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Icon: "dataUriProps.src" is required when type="data-uri"');
            }
            return null;
        }

        const Wrapper = onClick ? 'button' : 'span';
        return (
            <Wrapper
                type={onClick ? 'button' : undefined}
                data-testid={testID}
                className={containerClasses}
                style={containerStyle}
                onClick={disabled ? undefined : onClick}
                disabled={onClick ? disabled : undefined}
                aria-label={ariaLabel ?? name}
                aria-hidden={ariaHidden}
            >
                <Image
                    width={iconSize}
                    height={iconSize}
                    className={iconClasses}
                    loading="lazy"
                    unoptimized
                    {...dataUriProps}
                    alt={dataUriProps.alt ?? ariaLabel ?? name ?? ''}
                />
            </Wrapper>
        );
    }

    // 3. Lookup icon by name from library
    if (name) {
        const IconComponent = getIconComponent(name, type);

        if (IconComponent) {
            return (
                <span
                    data-testid={testID}
                    className={containerClasses}
                    style={containerStyle}
                    onClick={disabled ? undefined : onClick}
                    aria-label={ariaLabel ?? name}
                    aria-hidden={ariaHidden}
                >
                    <IconComponent
                        size={iconSize}
                        color={color}
                        strokeWidth={type === 'lucide' ? strokeWidth : undefined}
                        className={iconClasses}
                    />
                </span>
            );
        }

        // Icon not found - show warning in dev
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Icon "${name}" not found in ${type} library. Make sure the name is correct.`);
        }
    }

    // 4. Inline SVG children
    if (children) {
        return (
            <span
                data-testid={testID}
                className={containerClasses}
                style={containerStyle}
                onClick={disabled ? undefined : onClick}
                aria-label={ariaLabel ?? name}
                aria-hidden={ariaHidden}
            >
                <svg
                    width={iconSize}
                    height={iconSize}
                    viewBox="0 0 24 24"
                    fill={fill ?? 'none'}
                    stroke={stroke ?? 'currentColor'}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={iconClasses}
                    {...svgProps}
                >
                    {children}
                </svg>
            </span>
        );
    }

    // Nothing to render
    return null;
};

Icon.displayName = 'Icon';

export default Icon;
